<template>
  <div class="ai-chat-widget">
    <!-- Floating Chat Button -->
    <div
      v-if="!isOpen"
      @click="toggleChat"
      class="chat-button"
      :class="{ 'chat-button--loading': isLoading }"
    >
      <i v-if="!isLoading" class="fas fa-robot"></i>
      <i v-else class="fas fa-circle-notch fa-spin"></i>
      <!-- Notification badge for new messages -->
      <div v-if="hasUnreadMessages" class="notification-badge"></div>
    </div>

    <!-- Chat Window -->
    <div v-if="isOpen" class="chat-window">
      <!-- Chat Header -->
      <div class="chat-header">
        <div class="chat-header-content">
          <div class="chat-title">
            <i class="fas fa-robot"></i>
            <span>KylianERP AI Assistant</span>
          </div>
          <div class="chat-subtitle">
            <span v-if="isConnected" class="status-indicator connected">
              <i class="fas fa-circle"></i> Connected
            </span>
            <span v-else class="status-indicator disconnected">
              <i class="fas fa-circle"></i> Disconnected
            </span>
          </div>
        </div>
        <div class="header-actions">
          <button @click="clearChat" class="header-button" title="Clear chat">
            <i class="fas fa-trash"></i>
          </button>
          <button @click="toggleChat" class="close-button">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Chat Messages -->
      <div class="chat-messages" ref="messagesContainer">
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="message"
          :class="message.type"
        >
          <div class="message-content">
            <div v-if="message.type === 'user'" class="message-avatar">
              <i class="fas fa-user"></i>
            </div>
            <div v-else class="message-avatar ai-avatar">
              <i class="fas fa-robot"></i>
            </div>
            <div class="message-text">
              <div class="message-bubble">
                <div v-if="message.type === 'error'" class="error-message">
                  <i class="fas fa-exclamation-triangle"></i>
                  {{ message.text }}
                </div>
                <div v-else v-html="formatMessage(message.text)"></div>
              </div>
              <div class="message-time">
                {{ formatTime(message.timestamp) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Loading indicator -->
        <div v-if="isLoading" class="message ai">
          <div class="message-content">
            <div class="message-avatar ai-avatar">
              <i class="fas fa-robot"></i>
            </div>
            <div class="message-text">
              <div class="message-bubble loading">
                <div class="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Input -->
      <div class="chat-input-container">
        <div class="chat-input-wrapper">
          <input
            v-model="currentMessage"
            @keyup.enter="sendMessage"
            @keyup.esc="toggleChat"
            type="text"
            placeholder="Ask me anything about KylianERP..."
            class="chat-input"
            :disabled="isLoading || !isConnected"
          />
          <button
            @click="sendMessage"
            class="send-button"
            :disabled="!currentMessage.trim() || isLoading || !isConnected"
            :title="
              !isConnected
                ? 'AI service is currently unavailable'
                : 'Send message'
            "
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>

        <!-- Quick Actions -->
        <div class="quick-actions">
          <button
            v-for="(action, index) in quickActions"
            :key="index"
            @click="sendQuickMessage(action.text)"
            class="quick-action-btn"
            :disabled="isLoading || !isConnected"
          >
            {{ action.text }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AIChatWidget',
  data() {
    return {
      isOpen: false,
      isLoading: false,
      isConnected: true,
      currentMessage: '',
      messages: [],
      hasUnreadMessages: false,
      apiBaseUrl: 'https://b019054308b8.ngrok-free.app',
      quickActions: [
        { text: 'How to check in?' },
        { text: 'How to request leave?' },
        { text: 'How to view payslip?' },
        { text: 'How to update profile?' },
      ],
    };
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.hasUnreadMessages = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    async sendMessage() {
      if (!this.currentMessage.trim() || this.isLoading || !this.isConnected)
        return;

      const userMessage = this.currentMessage.trim();
      this.messages.push({
        type: 'user',
        text: userMessage,
        timestamp: new Date(),
      });

      this.currentMessage = '';
      this.isLoading = true;

      try {
        // Test connection first
        await this.testConnection();

        const response = await this.$axios.post(
          `${this.apiBaseUrl}/ask`,
          {
            question: userMessage,
          },
          {
            timeout: 30000, // 30 second timeout
          }
        );

        const aiResponse =
          response.data.answer ||
          response.data ||
          "I couldn't find relevant information for your question. Please contact Bassey on WhatsApp: 08102555913 or call: 09054969630 for more details. Thank you.";

        this.messages.push({
          type: 'ai',
          text: aiResponse,
          timestamp: new Date(),
        });

        this.saveChatHistory();
      } catch (error) {
        console.error('Error sending message:', error);

        let errorMessage = 'Sorry, I encountered an error. Please try again.';

        if (error.code === 'ECONNABORTED') {
          errorMessage =
            'Request timed out. Please check your connection and try again.';
        } else if (
          error.code === 'ERR_NETWORK' ||
          error.message.includes('CORS')
        ) {
          errorMessage =
            'CORS error: Unable to connect to AI service. Please contact the administrator to configure CORS settings.';
        } else if (error.response?.status === 404) {
          errorMessage =
            'AI service is currently unavailable. Please try again later.';
        } else if (error.response?.status >= 500) {
          errorMessage =
            'Server error. Please try again later or contact support.';
        }

        this.messages.push({
          type: 'error',
          text: errorMessage,
          timestamp: new Date(),
        });

        // Only set disconnected for actual server issues, not CORS
        if (!error.message.includes('CORS')) {
          this.isConnected = false;
        }
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    sendQuickMessage(text) {
      this.currentMessage = text;
      this.sendMessage();
    },

    async testConnection() {
      try {
        // Try to connect to the AI service
        await this.$axios.get(`${this.apiBaseUrl}/docs`, {
          timeout: 5000,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        this.isConnected = true;
      } catch (error) {
        console.error('Connection test failed:', error);
        // Don't immediately set disconnected for CORS errors
        if (error.code === 'ERR_NETWORK' || error.message.includes('CORS')) {
          // For CORS issues, we'll still allow sending messages
          // The actual API call will handle the error
          this.isConnected = true;
        } else {
          this.isConnected = false;
        }
        throw error;
      }
    },

    clearChat() {
      this.messages = [
        {
          type: 'ai',
          text:
            "Hello! I'm your KylianERP AI Assistant. How can I help you today?",
          timestamp: new Date(),
        },
      ];
      this.saveChatHistory();
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatMessage(text) {
      if (!text) return '';

      // Convert bold text (**text** to <strong>text</strong>)
      let formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Convert numbered lists (1. 2. 3. etc.)
      formattedText = formattedText.replace(
        /(\d+\.\s+)([^\n]+)/g,
        '<div class="numbered-item"><span class="number">$1</span><span class="content">$2</span></div>'
      );

      // Convert bullet points (- or * or •)
      formattedText = formattedText.replace(
        /^[\s]*[-*•]\s+([^\n]+)/gm,
        '<div class="bullet-item"><span class="bullet">•</span><span class="content">$1</span></div>'
      );

      // Convert line breaks to proper spacing
      formattedText = formattedText.replace(/\n\n/g, '</p><p>');
      formattedText = formattedText.replace(/\n/g, '<br>');

      // Wrap in paragraphs if not already wrapped
      if (
        !formattedText.startsWith('<div') &&
        !formattedText.startsWith('<p>')
      ) {
        formattedText = '<p>' + formattedText + '</p>';
      }

      return formattedText;
    },

    saveChatHistory() {
      try {
        const chatHistory = {
          messages: this.messages,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem(
          'kylianerp_chat_history',
          JSON.stringify(chatHistory)
        );
      } catch (error) {
        console.error('Error saving chat history:', error);
      }
    },

    loadChatHistory() {
      try {
        const saved = localStorage.getItem('kylianerp_chat_history');
        if (saved) {
          const chatHistory = JSON.parse(saved);
          // Only load if it's from today
          const savedDate = new Date(chatHistory.timestamp);
          const today = new Date();
          if (savedDate.toDateString() === today.toDateString()) {
            this.messages = chatHistory.messages;
          }
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
      }
    },
  },

  mounted() {
    this.loadChatHistory();

    // Add welcome message if no history
    if (this.messages.length === 0) {
      this.messages.push({
        type: 'ai',
        text:
          "Hello! I'm your KylianERP AI Assistant. How can I help you today?",
        timestamp: new Date(),
      });
    }

    // Test connection on mount
    this.testConnection();

    // Auto-save chat history every 30 seconds
    setInterval(() => {
      if (this.messages.length > 0) {
        this.saveChatHistory();
      }
    }, 30000);
  },

  watch: {
    isOpen(newVal) {
      if (!newVal && this.messages.length > 1) {
        this.hasUnreadMessages = true;
      }
    },
  },
};
</script>

<style scoped>
.ai-chat-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Floating Chat Button */
.chat-button {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  font-size: 24px;
  position: relative;
}

.chat-button:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
}

.chat-button--loading {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  background: #ff4757;
  border-radius: 50%;
  border: 2px solid white;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Chat Window */
.chat-window {
  width: 380px;
  height: 550px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Chat Header */
.chat-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-header-content {
  flex: 1;
}

.chat-title {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.chat-subtitle {
  font-size: 12px;
  opacity: 0.9;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
}

.status-indicator.connected i {
  color: #2ed573;
}

.status-indicator.disconnected i {
  color: #ff4757;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.header-button,
.close-button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  font-size: 14px;
}

.header-button:hover,
.close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Chat Messages */
.chat-messages {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  background: #f8f9fa;
}

.message {
  margin-bottom: 16px;
}

.message-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #6c757d;
  flex-shrink: 0;
}

.ai-avatar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.message-text {
  flex: 1;
  min-width: 0;
}

.message-bubble {
  background: white;
  padding: 12px 16px;
  border-radius: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  word-wrap: break-word;
  line-height: 1.4;
}

.message.user .message-bubble {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.message.error .message-bubble {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  color: #c53030;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-message i {
  color: #e53e3e;
}

.message-time {
  font-size: 11px;
  color: #6c757d;
  margin-top: 4px;
  margin-left: 4px;
}

/* Loading Animation */
.loading {
  background: #f8f9fa !important;
  color: #6c757d !important;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6c757d;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) {
  animation-delay: -0.32s;
}
.typing-indicator span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Chat Input */
.chat-input-container {
  padding: 16px;
  background: white;
  border-top: 1px solid #e9ecef;
}

.chat-input-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
}

.chat-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e9ecef;
  border-radius: 24px;
  outline: none;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.chat-input:focus {
  border-color: #667eea;
}

.chat-input:disabled {
  background: #f8f9fa;
  cursor: not-allowed;
}

.send-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.send-button:hover:not(:disabled) {
  transform: scale(1.05);
}

.send-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Quick Actions */
.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.quick-action-btn {
  padding: 6px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #495057;
}

.quick-action-btn:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #dee2e6;
}

.quick-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Message Formatting */
.message-bubble p {
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.message-bubble p:last-child {
  margin-bottom: 0;
}

.numbered-item {
  display: flex;
  margin-bottom: 8px;
  align-items: flex-start;
}

.numbered-item .number {
  font-weight: 600;
  color: #667eea;
  margin-right: 8px;
  min-width: 20px;
  flex-shrink: 0;
}

.numbered-item .content {
  flex: 1;
  line-height: 1.5;
}

.bullet-item {
  display: flex;
  margin-bottom: 8px;
  align-items: flex-start;
}

.bullet-item .bullet {
  color: #667eea;
  margin-right: 8px;
  font-weight: bold;
  min-width: 12px;
  flex-shrink: 0;
}

.bullet-item .content {
  flex: 1;
  line-height: 1.5;
}

/* Responsive Design */
@media (max-width: 480px) {
  .chat-window {
    width: calc(100vw - 40px);
    height: calc(100vh - 120px);
    position: fixed;
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 20px;
  }

  .chat-button {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }

  .quick-actions {
    justify-content: center;
  }
}

/* Scrollbar Styling */
.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
